import { FEATURE_KEYS } from '@/types';
import type { FeatureAdoptionRow, TenantFeatureState } from '@/types/platform';
import { FEATURE_REGISTRY, featureLabel } from '@/lib/featureRegistry';
import { ServiceError } from '../../contracts';
import type { PlatformFeatureService } from '../../platformContracts';
import { adminRoot, dataFor, delay, pctx, planOf, recordAudit, syncTenantFeatures } from './context';

function state(tenantId: string): TenantFeatureState {
  const t = dataFor(tenantId);
  const plan = planOf(t.planId);
  return { planId: plan.id, planName: plan.name, planFeatures: plan.features, overrides: { ...t.featureOverrides }, effective: { ...t.features } };
}

export const platformFeatures: PlatformFeatureService = {
  async registry() {
    pctx('platform.features.manage');
    return delay(FEATURE_REGISTRY);
  },
  async adoption() {
    pctx('platform.features.manage');
    const tenants = Object.values(adminRoot());
    const rows: FeatureAdoptionRow[] = FEATURE_KEYS.map((key) => ({
      key, label: featureLabel(key), totalTenants: tenants.length,
      enabledTenants: tenants.filter((t) => t.features[key]).length,
      overriddenTenants: tenants.filter((t) => key in t.featureOverrides).length,
    }));
    return delay(rows);
  },
  async getState(tenantId) {
    pctx('platform.features.manage');
    dataFor(tenantId); // 404s if the tenant id is unknown/belongs to nothing
    return delay(state(tenantId));
  },
  async setOverride(tenantId, key, value) {
    const { actor } = pctx('platform.features.manage');
    const t = dataFor(tenantId);
    if (!FEATURE_KEYS.includes(key)) throw new ServiceError('Unknown feature key', 'INVALID');
    const def = FEATURE_REGISTRY.find((f) => f.key === key);
    if (def?.core) throw new ServiceError(`${def.label} is core to every store and cannot be turned off.`, 'INVALID');
    const before = t.features[key];
    if (value === null) delete t.featureOverrides[key]; else t.featureOverrides[key] = value;
    syncTenantFeatures(tenantId);
    const after = t.features[key];
    const label = featureLabel(key);
    const summary = value === null ? `${label}: reverted to plan default (now ${after ? 'on' : 'off'})` : `${label}: ${value ? 'force-enabled' : 'force-disabled'} (was ${before ? 'on' : 'off'})`;
    recordAudit(actor, 'tenant.feature_override', 'TENANT', tenantId, t.name, summary);
    return delay(state(tenantId), 450);
  },
  async resetAll(tenantId) {
    const { actor } = pctx('platform.features.manage');
    const t = dataFor(tenantId);
    const n = Object.keys(t.featureOverrides).length;
    t.featureOverrides = {};
    syncTenantFeatures(tenantId);
    if (n) recordAudit(actor, 'tenant.feature_override', 'TENANT', tenantId, t.name, `Cleared all ${n} override${n === 1 ? '' : 's'} — back to plan defaults`);
    return delay(state(tenantId), 400);
  },
};
